import { FunctionComponent, useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import {
  Card,
  Container,
  Row,
  Col,
  ButtonGroup,
  Button,
  Modal,
  Form,
  Table,
  Spinner,
  Nav,
  Tab,
  Alert,
} from 'react-bootstrap'
import { Search, Download } from 'react-bootstrap-icons'
import Chart from 'react-google-charts'
import classnames from 'classnames'
import { format } from 'date-fns'

import CurrencyLabel from 'components/util/currencyLabel'
import { TransferIndicators } from './transferIndicators'
import { UserRole } from 'features/user/types'

import DatePicker from 'components/forms/datePicker'

import transferCss from './styles.module.scss'
import TransfersSkeleton from './transfersSkeleton'

import * as transferApi from './api.client'
import * as hospitalApi from '../hospitals/api.client'
import * as doctorApi from '../doctors/api.client'
import {
  CONVENIO_FILTER_ALL,
  CONVENIO_FILTER_CONVENIOS_OU_PARTICULAR,
  Convenio,
  ConvenioFilter,
  isParticularTransfer,
  Transfer,
  TransferStatus,
} from './types'
import { HospitalAvailable } from 'features/hospitals/types'
import { Doctor } from 'features/doctors/types'
import { exportarCsv } from 'utilities/csv.util'
import { getDateFromString } from 'utilities/date.util'
import { toCurrency } from 'utilities/number.util'
import { AccessRequestStatus } from '../accessRequest/types'
import { getUserProfile, getUsername } from '../user/auth'
import { stringNullOrEmpty } from '../../utilities/string.util'

const compareByDataProcedimento = (a: Transfer, b: Transfer) =>
  getDateFromString(b.dataProcedimento).getTime() -
  getDateFromString(a.dataProcedimento).getTime()

const statusColors = new Map()
statusColors.set(TransferStatus.PAGO, '#2CAF7D')
statusColors.set(TransferStatus.LIBERADO, '#21a9f5')
statusColors.set(TransferStatus.PROCESSANDO, '#36c3d9')
statusColors.set(TransferStatus.PENDENTE, '#F7C61C')
statusColors.set(TransferStatus.GLOSADO, '#D6103B')

const detailSubtitlesByStatus = new Map<TransferStatus, string>([
  [TransferStatus.PENDENTE, 'Contas a receber.'],
  [TransferStatus.PROCESSANDO, 'Fila de pagamento.'],
  [TransferStatus.LIBERADO, 'Próximo pagamento.'],
  [TransferStatus.PAGO, 'Pago.'],
  [TransferStatus.GLOSADO, 'Glosado.'],
])

const MASK = 'dd-MM-yyyy'
const TRANSFER_FILTER_MIN_DATE = new Date(2025, 0, 1)

const STATUS_SELECTION_ORDER: TransferStatus[] = [
  TransferStatus.PAGO,
  TransferStatus.LIBERADO,
  TransferStatus.PROCESSANDO,
  TransferStatus.PENDENTE,
  TransferStatus.GLOSADO,
]

const getDefaultStatusWithTransfers = (
  indicators: TransferIndicators
): TransferStatus =>
  STATUS_SELECTION_ORDER.find(
    (status) => indicators.getTotalByStatus(status) > 0
  ) ?? TransferStatus.PAGO

const Transfers: FunctionComponent = () => {
  const navigate = useNavigate()

  const [transfers, setTransfers] = useState<Transfer[]>([])
  const [transfersForChart, setTransfersForChart] = useState<any[][]>([])
  const [transferStatusSelected, setTransferStatusSelected] = useState(
    TransferStatus.PAGO
  )
  const [transferConvenioSelected, setTransferConvenioSelected] =
    useState<ConvenioFilter>(CONVENIO_FILTER_ALL)
  const [indicators, setIndicators] = useState<TransferIndicators>(
    new TransferIndicators()
  )
  const [transfersLoaded, setTransfersLoaded] = useState(false)
  const [hospitals, setHospitals] = useState<HospitalAvailable[]>([])
  const [doctors, setDoctors] = useState<Doctor[]>([])
  const [selectedHospitalId, setSelectedHospitalId] = useState(0)
  const [selectedDoctorId, setSelectedDoctorId] = useState(0)
  const [filter, setFilter] = useState({ start: new Date(), end: new Date() })
  const [openReportDetail, setOpenReportDetail] = useState(false)
  const [reportAtendimentoFilter, setReportAtendimentoFilter] = useState('')
  const [pendingReportTab, setPendingReportTab] = useState<
    'particular' | 'convenios'
  >('particular')
  const [chartColors, setChartColors] = useState<string[]>([])
  const [errorMessage, setErrorMessage] = useState({ show: false, message: '' })
  const [atendimento, setAtendimento] = useState<string>()

  const setTransfersAndBuildIndicators = useCallback(
    (transfers: Transfer[]) => {
      // TODO: Deixar o filtro de repasses com valores negativos configurável
      const transfersWithoutNegatives = transfers.filter(
        (t) => t.valorRepasse > 0
      )

      const indicators = TransferIndicators.build(
        transfersWithoutNegatives,
        transferConvenioSelected
      )
      const statusToSelect = getDefaultStatusWithTransfers(indicators)

      setTransfers(transfersWithoutNegatives)
      setIndicators(indicators)
      setTransferStatusSelected(statusToSelect)
      buildAndSetTransfersForChart(indicators, statusToSelect)
    },
    [transferConvenioSelected]
  )

  const loadTransfersByHospital = useCallback(
    async (hospitalId: number, startDate: Date, endDate: Date) => {
      setTransfersLoaded(false)

      try {
        const transfers = await transferApi.getTransfersByHospital(
          hospitalId,
          format(startDate, MASK),
          format(endDate, MASK)
        )
        setTransfersAndBuildIndicators(transfers)
      } catch (error) {
        setTransfersLoaded(true)
        const errorMessage = 'Ocorreu um erro ao consultar os repasses médicos.'
        console.error(errorMessage, error)
        setErrorMessage({
          show: true,
          message: errorMessage,
        })
      }
    },
    [setTransfersAndBuildIndicators]
  )

  const loadHospitals = useCallback(async () => {
    const initialFilterDates = getInitialFilterDates()

    const hospitals = await hospitalApi.getAvailableHospitals()

    const hospitalDoctorHasAccess = hospitals.filter(
      (hospital) =>
        AccessRequestStatus[hospital.accessRequestStatus] ===
        AccessRequestStatus.APPROVED.toString()
    )

    const firstHospitalId =
      hospitalDoctorHasAccess.length === 0 ? 0 : hospitalDoctorHasAccess[0].id

    setHospitals(hospitalDoctorHasAccess)
    setSelectedHospitalId(firstHospitalId)
    setFilter({
      start: initialFilterDates[0],
      end: initialFilterDates[1],
    })

    loadTransfersByHospital(
      firstHospitalId,
      initialFilterDates[0],
      initialFilterDates[1]
    )
  }, [loadTransfersByHospital])

  const loadTransfersByDoctor = useCallback(
    async (doctorId: number, startDate: Date, endDate: Date) => {
      setTransfersLoaded(false)

      try {
        const transfers = await transferApi.getTransfersByDoctor(
          doctorId,
          format(startDate, MASK),
          format(endDate, MASK)
        )
        setTransfersAndBuildIndicators(transfers)
      } catch (error) {
        setTransfersLoaded(true)
        const errorMessage = 'Ocorreu um erro ao consultar os repasses médicos.'
        setErrorMessage({
          show: true,
          message: errorMessage,
        })
      }
    },
    [setTransfersAndBuildIndicators]
  )

  const loadTransfersByDoctorAndServiceNumber = useCallback(
    async (doctorId: number, serviceNumber: number) => {
      setTransfersLoaded(false)

      try {
        if (atendimento !== undefined) {
          const transfers =
            await transferApi.getTransfersByDoctorAndServiceNumber(
              doctorId,
              serviceNumber
            )
          setTransfersAndBuildIndicators(transfers)
        }
      } catch (error) {
        setTransfersLoaded(true)
        const errorMessage = 'Ocorreu um erro ao consultar os repasses médicos.'
        setErrorMessage({
          show: true,
          message: errorMessage,
        })
      }
    },
    [atendimento, setTransfersAndBuildIndicators]
  )

  const loadDoctors = useCallback(async () => {
    const initialFilterDates = getInitialFilterDates()
    const doctors = await doctorApi.getDoctorsRelatedToHospital()
    const firstDoctorId = doctors.length === 0 ? 0 : doctors[0].id

    setDoctors(doctors)
    setSelectedDoctorId(firstDoctorId)
    setFilter({
      start: initialFilterDates[0],
      end: initialFilterDates[1],
    })

    loadTransfersByDoctor(
      firstDoctorId,
      initialFilterDates[0],
      initialFilterDates[1]
    )
  }, [loadTransfersByDoctor])

  const getInitialFilterDates = (): [Date, Date] => {
    const now = new Date()
    const yearAgo = new Date()
    yearAgo.setFullYear(yearAgo.getFullYear() - 1)

    const start =
      yearAgo < TRANSFER_FILTER_MIN_DATE ? TRANSFER_FILTER_MIN_DATE : yearAgo

    return [start, now]
  }

  const loadTransfers = (startDate: Date, endDate: Date) => {
    if (selectedHospitalId && !atendimento && loggedUserIsDoctor()) {
      loadTransfersByHospital(selectedHospitalId, startDate, endDate)
    } else if (selectedDoctorId && !atendimento && !loggedUserIsDoctor()) {
      loadTransfersByDoctor(selectedDoctorId, startDate, endDate)
    } else {
      loadTransfersByDoctorAndServiceNumber(
        selectedDoctorId,
        // TODO: ajustar os tipos
        atendimento as unknown as number
      )
    }
  }

  const buildIndicatorsByConvenio = (convenio: ConvenioFilter) => {
    const indicators = TransferIndicators.build(transfers, convenio)
    const statusToSelect = getDefaultStatusWithTransfers(indicators)
    setIndicators(indicators)
    setTransferStatusSelected(statusToSelect)
    buildAndSetTransfersForChart(indicators, statusToSelect)
  }

  const updateTransfersOnChart = (status: TransferStatus) => {
    buildAndSetTransfersForChart(indicators, status)
  }

  const hasTransfersForStatus = (status: TransferStatus) =>
    indicators.getTotalByStatus(status) > 0

  const getStatusCardProps = (status: TransferStatus) => {
    const hasTransfers = hasTransfersForStatus(status)

    return {
      className: classnames('mb-2', transferCss.headerCard, {
        [transferCss.headerCardDisabled]: !hasTransfers,
      }),
      onClick: hasTransfers
        ? () => updateTransfersOnChart(status)
        : undefined,
    }
  }

  const buildAndSetTransfersForChart = (
    indicators: TransferIndicators,
    transferStatus: TransferStatus
  ) => {
    let transfersTotalsByMonth =
      indicators.getTotalGroupedByMonthAndYearFrom(transferStatus)

    if (transfersTotalsByMonth.size === 0) {
      setTransfersLoaded(true)
      setTransfersForChart([])

      return
    }

    let chartDescription = new Array<any>()
    chartDescription.push('Mês/Ano com base na data do procedimento')
    chartDescription.push('')
    chartDescription.push({ type: 'string', role: 'annotation' })

    let transfersForChart = new Array<Array<any>>(chartDescription)

    Array.from(transfersTotalsByMonth.entries()).forEach((entry) => {
      let transferOfMonth: Array<any> = new Array<any>()
      transferOfMonth.push(entry[0])
      transferOfMonth.push(entry[1])
      transferOfMonth.push(toCurrency(entry[1]).replace(/^R\$\s*/, ''))
      transfersForChart.push(transferOfMonth)
    })

    const chartColors = []
    chartColors.push(statusColors.get(transferStatus) as string)

    setTransfersForChart(transfersForChart)
    setTransfersLoaded(true)
    setChartColors(chartColors)
    setTransferStatusSelected(transferStatus)
  }

  const handleFilterChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    inputValue?: any
  ) => {
    const { name, value } = event.currentTarget

    setFilter({
      ...filter,
      [name]: inputValue !== undefined ? inputValue : value,
    })
  }

  const loggedUserIsDoctor = (): boolean => getUserProfile() === UserRole.DOCTOR

  const changeConvenio = (convenio: ConvenioFilter) => {
    setTransferConvenioSelected(convenio)
    buildIndicatorsByConvenio(convenio)
  }

  const changeSelectedHospital = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const { value } = event.currentTarget
    setSelectedHospitalId(Number.parseInt(value))
  }

  const changeSelectedDoctor = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const { value } = event.currentTarget
    const selectedDoctorId = Number.parseInt(value)

    setSelectedDoctorId(selectedDoctorId)
    setAtendimento('')
    loadTransfersByDoctor(selectedDoctorId, filter.start, filter.end)
  }

  const openHospitalPage = () => navigate('/hospitals')

  const downloadAll = () => {
    downloadCsv(transfers.sort((t1, t2) => (t1.status > t2.status ? 1 : -1)))
  }

  const downloadByStatus = () => {
    downloadCsv(
      transfers
        .filter(
          (transfer) =>
            transfer.status === transferStatusSelected &&
            (transferConvenioSelected === CONVENIO_FILTER_ALL ||
              (transferConvenioSelected === Convenio.SUS
                ? transfer.convenio === Convenio.SUS
                : transfer.convenio !== Convenio.SUS))
        )
        .sort(compareByDataProcedimento)
    )
  }

  const downloadCsv = (transfers: Array<Transfer>) => {
    const isPendingReport = transferStatusSelected === TransferStatus.PENDENTE

    let arquivoCsv: string
    if (transferStatusSelected === TransferStatus.PAGO) {
      arquivoCsv =
        'Atendimento;Status;Convenio;Procedimento;Data do procedimento;Data da Alta;Data do fechamento;Data do pagamento;Valor\n'
    } else if (isPendingReport) {
      arquivoCsv =
        'Atendimento;Status;Convenio;Procedimento;Data do procedimento;Data da Alta;Data Protocolo;Classe do título;Status glosa título;Valor\n'
    } else {
      arquivoCsv =
        'Atendimento;Status;Convenio;Procedimento;Data do procedimento;Data da Alta;Data Protocolo;Valor\n'
    }

    transfers.forEach((t: Transfer) => {
      arquivoCsv +=
        t.numeroAtendimento === null ? 'N/A;' : t.numeroAtendimento + ';'
      arquivoCsv += t.status + ';'
      arquivoCsv += t.descricaoConvenio + ';'
      arquivoCsv +=
        (t.descricaoProcedimento !== null
          ? t.descricaoProcedimento
          : t.observacaoRepasse) + ';'
      arquivoCsv += t.dataProcedimento + ';'
      arquivoCsv += t.dataAlta + ';'
      arquivoCsv +=
        (transferStatusSelected === TransferStatus.PAGO
          ? t.dataFechamentoRepasse
          : t.dataProtocolo) + ';'

      if (transferStatusSelected === TransferStatus.PAGO) {
        arquivoCsv += t.dataPagamento + ';'
      } else if (isPendingReport) {
        arquivoCsv += (t.classeDoTitulo ?? '') + ';'
        arquivoCsv += (t.statusGlosaTituloReceber ?? '') + ';'
      }

      arquivoCsv += t.valorRepasse + ';\n'
    })

    exportarCsv(arquivoCsv, 'repasses')
  }

  useEffect(() => {
    if (loggedUserIsDoctor()) {
      loadHospitals()
    }

    if (!loggedUserIsDoctor()) {
      loadDoctors()
    }
  }, [loadDoctors, loadHospitals])

  useEffect(() => {
    if (!openReportDetail) {
      setReportAtendimentoFilter('')
      return
    }

    setPendingReportTab('particular')
  }, [openReportDetail])

  const transfersForStatusReport = transfers.filter(
    (transfer) =>
      transfer.status === transferStatusSelected &&
      (transferConvenioSelected === CONVENIO_FILTER_ALL ||
        (transferConvenioSelected === Convenio.SUS
          ? transfer.convenio === Convenio.SUS
          : transfer.convenio !== Convenio.SUS))
  )

  const matchesReportAtendimentoFilter = (transfer: Transfer) => {
    const term = reportAtendimentoFilter.trim()

    if (!term) {
      return true
    }

    if (transfer.numeroAtendimento === null) {
      return false
    }

    return String(transfer.numeroAtendimento).includes(term)
  }

  const filteredTransfersForStatusReport = transfersForStatusReport
    .filter(matchesReportAtendimentoFilter)
    .sort(compareByDataProcedimento)

  const filteredReportTotal = filteredTransfersForStatusReport.reduce(
    (sum, transfer) => sum + transfer.valorRepasse,
    0
  )

  const reportFooterColSpanNonPending =
    transferStatusSelected === TransferStatus.PAGO ? 8 : 7

  const hasTransferContext = selectedHospitalId > 0 || selectedDoctorId > 0
  const showTransfersSkeleton = !transfersLoaded && hasTransferContext

  return (
    <Container
      fluid
      className={classnames(
        transferCss.contentHeigth,
        transferCss.pageLayout,
        (showTransfersSkeleton ||
          (transfersLoaded && transfersForChart.length > 0)) &&
          transferCss.pageLayoutFilled
      )}
    >
      {loggedUserIsDoctor() && selectedHospitalId === 0 && (
        <Alert variant="success" className={transferCss.noAccessMessage}>
          <Alert.Heading>Olá {getUsername()}</Alert.Heading>
          Você ainda não possui acesso aos repasses de nenhum hospital.
          <hr />
          <div className="d-flex justify-content-end">
            <Button
              onClick={() => openHospitalPage()}
              variant="outline-success"
            >
              Solicitar acesso
            </Button>
          </div>
        </Alert>
      )}

      {!loggedUserIsDoctor() && selectedDoctorId === 0 && (
        <Alert variant="success" className={transferCss.noAccessMessage}>
          <Alert.Heading>Olá {getUsername()}</Alert.Heading>
          Selecione um médico para consultar os repasses.
        </Alert>
      )}

      {errorMessage.show && (
        <Alert variant="danger" style={{ marginTop: 20 }}>
          {errorMessage.message}
        </Alert>
      )}

      {(selectedHospitalId > 0 || selectedDoctorId > 0) && (
        <Form className={transferCss.filterForm}>
          <Row className={transferCss.filterRow}>
            <Col
              xs={12}
              lg={3}
              className={classnames(
                transferCss.headerColSelect,
                transferCss.filterSelectCol
              )}
            >
              {loggedUserIsDoctor() && (
                <Form.Select
                  className="mb-2 mr-sm-2"
                  onChange={changeSelectedHospital}
                >
                  {hospitals.map((hospital, index) => (
                    <option key={index} value={hospital.id}>
                      {hospital.name}
                    </option>
                  ))}
                </Form.Select>
              )}

              {!loggedUserIsDoctor() && (
                <Form.Select
                  className="mb-2 mr-sm-2"
                  onChange={changeSelectedDoctor}
                >
                  {doctors?.map((doctor, index) => (
                    <option key={index} value={doctor.id}>
                      {doctor.name}
                    </option>
                  ))}
                </Form.Select>
              )}
            </Col>

            <Col
              xs={6}
              lg="auto"
              className={classnames(
                transferCss.headerCol,
                transferCss.filterDateCol
              )}
            >
              <DatePicker
                name="start"
                value={filter.start}
                onChange={handleFilterChange}
                placeholder="Data inicial"
                disabled={!stringNullOrEmpty(atendimento)}
                minDate={TRANSFER_FILTER_MIN_DATE}
              ></DatePicker>
            </Col>

            <Col
              xs={6}
              lg="auto"
              className={classnames(
                transferCss.headerCol,
                transferCss.filterDateCol
              )}
            >
              <DatePicker
                name="end"
                value={filter.end}
                onChange={handleFilterChange}
                placeholder="Data final"
                disabled={!stringNullOrEmpty(atendimento)}
                minDate={TRANSFER_FILTER_MIN_DATE}
              ></DatePicker>
            </Col>

            <Col
              xs={12}
              lg="auto"
              className={classnames(
                transferCss.headerCol,
                transferCss.filterAtendimentoCol
              )}
            >
              <Form.Control
                type="number"
                name="name"
                placeholder="Atendimento"
                value={atendimento}
                onChange={(e) => setAtendimento(e.currentTarget.value)}
              />
            </Col>

            <Col
              xs={12}
              lg="auto"
              className={classnames(
                transferCss.headerCol,
                transferCss.filterActionsCol
              )}
            >
              <ButtonGroup className={transferCss.filterButtonGroup}>
                <Button
                  type="button"
                  className={classnames('mb-2', transferCss.buttonSearch)}
                  variant="secondary"
                  onClick={() => loadTransfers(filter.start, filter.end)}
                >
                  {!transfersLoaded && (
                    <div>
                      <Spinner animation="border" size="sm" /> Buscando...
                    </div>
                  )}
                  {transfersLoaded && (
                    <div>
                      <Search /> Buscar repasses
                    </div>
                  )}
                </Button>

                <Button
                  type="button"
                  className={classnames('mb-2', transferCss.buttonSearch)}
                  variant="secondary"
                  onClick={downloadAll}
                >
                  <div>
                    <Download />
                  </div>
                </Button>
              </ButtonGroup>
            </Col>
          </Row>
        </Form>
      )}

      {!showTransfersSkeleton &&
        !loggedUserIsDoctor() &&
        transfersLoaded &&
        transfersForChart.length === 0 && (
          <Alert variant="warning" className={transferCss.noAccessMessage}>
            Não encontramos nenhum repasse.
          </Alert>
        )}

      {showTransfersSkeleton && (
        <TransfersSkeleton showConvenioTabs={selectedHospitalId > 0} />
      )}

      {!showTransfersSkeleton && selectedHospitalId > 0 && (
        <Nav variant="pills" style={{ marginBottom: '3px' }}>
          <Nav.Item>
            <Button
              type="button"
              variant="secondary"
              className={
                transferConvenioSelected === CONVENIO_FILTER_ALL
                  ? transferCss.buttonSelectedConvenio
                  : transferCss.buttonConvenio
              }
              onClick={() => changeConvenio(CONVENIO_FILTER_ALL)}
            >
              Todos
            </Button>
          </Nav.Item>
          <Nav.Item>
            <Button
              type="button"
              variant="secondary"
              className={
                transferConvenioSelected === Convenio.SUS
                  ? transferCss.buttonSelectedConvenio
                  : transferCss.buttonConvenio
              }
              onClick={() => changeConvenio(Convenio.SUS)}
            >
              SUS
            </Button>
          </Nav.Item>
          <Nav.Item>
            <Button
              type="button"
              variant="secondary"
              className={
                transferConvenioSelected ===
                CONVENIO_FILTER_CONVENIOS_OU_PARTICULAR
                  ? transferCss.buttonSelectedConvenio
                  : transferCss.buttonConvenio
              }
              onClick={() =>
                changeConvenio(CONVENIO_FILTER_CONVENIOS_OU_PARTICULAR)
              }
            >
              Convênios ou Particular
            </Button>
          </Nav.Item>
        </Nav>
      )}

      {!showTransfersSkeleton &&
        loggedUserIsDoctor() &&
        selectedHospitalId !== 0 &&
        transfersLoaded &&
        transfersForChart.length === 0 && (
          <Alert variant="warning" className={transferCss.noAccessMessage}>
            Não encontramos nenhum repasse.
          </Alert>
        )}

      {!showTransfersSkeleton &&
        transfersLoaded &&
        transfersForChart.length > 0 && (
        <Row>
          <Col
            className={classnames(
              transferCss.cardCol,
              transferCss.cardPendingPadding
            )}
          >
            <Card {...getStatusCardProps(TransferStatus.PENDENTE)}>
              <Card.Body className={transferCss.cardBody}>
                <Card.Title className={transferCss.cardTitle}>
                  Contas a receber
                </Card.Title>
                <Card.Text className={transferCss.pendingCardText}>
                  <CurrencyLabel>
                    {indicators.getTotalByStatus(TransferStatus.PENDENTE)}
                  </CurrencyLabel>
                </Card.Text>
                <p className={transferCss.cardDescription}>
                  Aguardando pagamento dos convênios e/ou cartão de créditos.
                </p>
              </Card.Body>
            </Card>
          </Col>
          <Col
            className={classnames(
              transferCss.cardCol,
              transferCss.cardGlossedAndReleasedPadding
            )}
          >
            <Card {...getStatusCardProps(TransferStatus.GLOSADO)}>
              <Card.Body className={transferCss.cardBody}>
                <Card.Title className={transferCss.cardTitle}>
                  Glosado
                </Card.Title>
                <Card.Text className={transferCss.glossedCardText}>
                  <CurrencyLabel>
                    {indicators.getTotalByStatus(TransferStatus.GLOSADO)}
                  </CurrencyLabel>
                </Card.Text>
                <p className={transferCss.cardDescription}>
                  Sem possibilidade de recebimento.
                </p>
              </Card.Body>
            </Card>
          </Col>
          <Col
            className={classnames(
              transferCss.cardCol,
              transferCss.cardProcessingPadding
            )}
          >
            <Card {...getStatusCardProps(TransferStatus.PROCESSANDO)}>
              <Card.Body className={transferCss.cardBody}>
                <Card.Title className={transferCss.cardTitle}>
                  Fila de pagamento
                </Card.Title>
                <Card.Text className={transferCss.processingCardText}>
                  <CurrencyLabel>
                    {indicators.getTotalByStatus(TransferStatus.PROCESSANDO)}
                  </CurrencyLabel>
                </Card.Text>
                <p className={transferCss.cardDescription}>
                  Valor já recebido do convênio e aguardando liberação do
                  repasse.
                </p>
              </Card.Body>
            </Card>
          </Col>
          <Col
            className={classnames(
              transferCss.cardCol,
              transferCss.cardGlossedAndReleasedPadding
            )}
          >
            <Card {...getStatusCardProps(TransferStatus.LIBERADO)}>
              <Card.Body className={transferCss.cardBody}>
                <Card.Title className={transferCss.cardTitle}>
                  Próximo pagamento
                </Card.Title>
                <Card.Text className={transferCss.releasedCardText}>
                  <CurrencyLabel>
                    {indicators.getTotalByStatus(TransferStatus.LIBERADO)}
                  </CurrencyLabel>
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>
          <Col
            className={(transferCss.paidCardCol, transferCss.cardPaidPadding)}
          >
            <Card {...getStatusCardProps(TransferStatus.PAGO)}>
              <Card.Body className={transferCss.cardBody}>
                <Card.Title className={transferCss.cardTitle}>Pago</Card.Title>
                <Card.Text className={transferCss.paidCardText}>
                  <CurrencyLabel>
                    {indicators.getTotalByStatus(TransferStatus.PAGO)}
                  </CurrencyLabel>
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {!showTransfersSkeleton &&
        transfersLoaded &&
        transfersForChart.length > 0 && (
        <div className={transferCss.mainContentArea}>
          <Row className={transferCss.chartRow}>
            <Col className={transferCss.chartCol}>
              <Card className={transferCss.chartCard}>
                <Row>
                  <Col xs={7}>
                    <h5
                      className={classnames(
                        transferCss.mainLabelsColor,
                        transferCss.chartTitle
                      )}
                    >
                      Detalhamento repasses:{' '}
                      <span
                        style={{
                          color: statusColors.get(transferStatusSelected),
                        }}
                      >
                        {detailSubtitlesByStatus.get(transferStatusSelected)}
                      </span>
                    </h5>
                  </Col>

                  <Col xs={5} className={transferCss.reportColButton}>
                    <ButtonGroup>
                      <Button
                        variant="secondary"
                        onClick={() => setOpenReportDetail(true)}
                        style={{
                          backgroundColor: statusColors.get(
                            transferStatusSelected
                          ),
                          borderColor: statusColors.get(transferStatusSelected),
                        }}
                        className={transferCss.reportButton}
                      >
                        Relatório
                      </Button>

                      <Button
                        variant="secondary"
                        onClick={() => downloadByStatus()}
                        style={{
                          backgroundColor: statusColors.get(
                            transferStatusSelected
                          ),
                          borderColor: statusColors.get(transferStatusSelected),
                        }}
                        className={transferCss.reportButton}
                      >
                        <div>
                          <Download />
                        </div>
                      </Button>
                    </ButtonGroup>
                  </Col>
                </Row>

                <Modal
                dialogClassName={transferCss.reportModal}
                size="lg"
                show={openReportDetail}
                onHide={() => setOpenReportDetail(false)}
              >
                <Modal.Header closeButton className={transferCss.reportModalHeader}>
                  <Modal.Title className={transferCss.mainLabelsColor}>
                    Relatório de repasses:{' '}
                    <span
                      style={{
                        color: statusColors.get(transferStatusSelected),
                      }}
                    >
                      {detailSubtitlesByStatus.get(transferStatusSelected)}
                    </span>
                  </Modal.Title>
                </Modal.Header>
                <Modal.Body className={transferCss.reportModalBody}>
                  <div className={transferCss.reportModalStickyHeader}>
                    <Form.Group
                      className={transferCss.reportAtendimentoFilter}
                      controlId="report-atendimento-filter"
                    >
                      <Form.Label>Atendimento</Form.Label>
                      <Form.Control
                        type="number"
                        placeholder="Filtrar por número de atendimento"
                        value={reportAtendimentoFilter}
                        onChange={(e) =>
                          setReportAtendimentoFilter(e.currentTarget.value)
                        }
                      />
                    </Form.Group>
                  </div>

                  <div className={transferCss.reportModalContent}>
                  {transferStatusSelected === TransferStatus.PENDENTE ? (
                    <Tab.Container
                      activeKey={pendingReportTab}
                      onSelect={(k) =>
                        k &&
                        setPendingReportTab(k as 'particular' | 'convenios')
                      }
                    >
                      <Nav
                        variant="tabs"
                        className={classnames('mb-3', transferCss.reportModalTabs)}
                      >
                        <Nav.Item>
                          <Nav.Link eventKey="particular">Particular</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                          <Nav.Link eventKey="convenios">Convênios</Nav.Link>
                        </Nav.Item>
                      </Nav>
                      <Tab.Content>
                        <Tab.Pane eventKey="particular">
                          <Table striped bordered hover size="sm">
                            <thead>
                              <tr>
                                <th>Atendimento</th>
                                <th>Status</th>
                                <th>Convênio</th>
                                <th>Procedimento</th>
                                <th>Data do procedimento</th>
                                <th>Data da alta</th>
                                <th>Data do protocolo</th>
                                <th>Classe do título</th>
                                <th>Valor</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredTransfersForStatusReport
                                .filter(isParticularTransfer)
                                .map((transfer, index) => (
                                  <tr key={index}>
                                    <td>{transfer.numeroAtendimento}</td>
                                    <td>{transfer.descricaoStatus}</td>
                                    <td>{transfer.descricaoConvenio}</td>
                                    <td>
                                      {transfer.descricaoProcedimento !== null
                                        ? transfer.descricaoProcedimento
                                        : transfer.observacaoRepasse}
                                    </td>
                                    <td>{transfer.dataProcedimento}</td>
                                    <td>{transfer.dataAlta}</td>
                                    <td>{transfer.dataProtocolo}</td>
                                    <td>{transfer.classeDoTitulo ?? ''}</td>
                                    <td>
                                      <CurrencyLabel>
                                        {transfer.valorRepasse}
                                      </CurrencyLabel>
                                    </td>
                                  </tr>
                                ))}
                              <tr>
                                <td colSpan={8}></td>
                                <td>
                                  <b>
                                    <CurrencyLabel>
                                      {filteredTransfersForStatusReport
                                        .filter(isParticularTransfer)
                                        .reduce(
                                          (sum, t) => sum + t.valorRepasse,
                                          0
                                        )}
                                    </CurrencyLabel>
                                  </b>
                                </td>
                              </tr>
                            </tbody>
                          </Table>
                        </Tab.Pane>
                        <Tab.Pane eventKey="convenios">
                          <Table striped bordered hover size="sm">
                            <thead>
                              <tr>
                                <th>Atendimento</th>
                                <th>Status</th>
                                <th>Convênio</th>
                                <th>Procedimento</th>
                                <th>Data do procedimento</th>
                                <th>Data da alta</th>
                                <th>Data do protocolo</th>
                                <th>Classe do título</th>
                                <th>Status glosa título</th>
                                <th>Valor</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredTransfersForStatusReport
                                .filter((t) => !isParticularTransfer(t))
                                .map((transfer, index) => (
                                  <tr key={index}>
                                    <td>{transfer.numeroAtendimento}</td>
                                    <td>{transfer.descricaoStatus}</td>
                                    <td>{transfer.descricaoConvenio}</td>
                                    <td>
                                      {transfer.descricaoProcedimento !== null
                                        ? transfer.descricaoProcedimento
                                        : transfer.observacaoRepasse}
                                    </td>
                                    <td>{transfer.dataProcedimento}</td>
                                    <td>{transfer.dataAlta}</td>
                                    <td>{transfer.dataProtocolo}</td>
                                    <td>{transfer.classeDoTitulo ?? ''}</td>
                                    <td>
                                      {transfer.statusGlosaTituloReceber ?? ''}
                                    </td>
                                    <td>
                                      <CurrencyLabel>
                                        {transfer.valorRepasse}
                                      </CurrencyLabel>
                                    </td>
                                  </tr>
                                ))}
                              <tr>
                                <td colSpan={9}></td>
                                <td>
                                  <b>
                                    <CurrencyLabel>
                                      {filteredTransfersForStatusReport
                                        .filter((t) => !isParticularTransfer(t))
                                        .reduce(
                                          (sum, t) => sum + t.valorRepasse,
                                          0
                                        )}
                                    </CurrencyLabel>
                                  </b>
                                </td>
                              </tr>
                            </tbody>
                          </Table>
                        </Tab.Pane>
                      </Tab.Content>
                    </Tab.Container>
                  ) : (
                    <Table striped bordered hover size="sm">
                      <thead>
                        <tr>
                          <th>Atendimento</th>
                          <th>Status</th>
                          <th>Convênio</th>
                          <th>Procedimento</th>
                          <th>Data do procedimento</th>
                          <th>Data da alta</th>
                          {transferStatusSelected !== TransferStatus.PAGO && (
                            <th>Data do protocolo</th>
                          )}
                          {transferStatusSelected === TransferStatus.PAGO && (
                            <th>Data do fechamento</th>
                          )}
                          {transferStatusSelected === TransferStatus.PAGO && (
                            <th>Data do pagamento</th>
                          )}
                          <th>Valor</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredTransfersForStatusReport.map(
                          (transfer, index) => (
                            <tr key={index}>
                              <td>{transfer.numeroAtendimento}</td>
                              <td>{transfer.descricaoStatus}</td>
                              <td>{transfer.descricaoConvenio}</td>
                              <td>
                                {transfer.descricaoProcedimento !== null
                                  ? transfer.descricaoProcedimento
                                  : transfer.observacaoRepasse}
                              </td>
                              <td>{transfer.dataProcedimento}</td>
                              <td>{transfer.dataAlta}</td>
                              <td>
                                {transferStatusSelected !== TransferStatus.PAGO
                                  ? transfer.dataProtocolo
                                  : transfer.dataFechamentoRepasse}
                              </td>
                              {transferStatusSelected === TransferStatus.PAGO && (
                                <td>{transfer.dataPagamento}</td>
                              )}
                              <td>
                                <CurrencyLabel>
                                  {transfer.valorRepasse}
                                </CurrencyLabel>
                              </td>
                            </tr>
                          )
                        )}
                        <tr>
                          <td colSpan={reportFooterColSpanNonPending}></td>
                          <td>
                            <b>
                              <CurrencyLabel>
                                {filteredReportTotal}
                              </CurrencyLabel>
                            </b>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  )}
                  </div>
                </Modal.Body>
              </Modal>

                <div className={transferCss.chartWrapper}>
                  <Chart
                    height="100%"
                    chartType="LineChart"
                    data={transfersForChart}
                    options={{
                      colors: chartColors,
                      legend: { position: 'none' },
                      chartArea: { width: '90%', height: '70%', top: 48 },
                      pointSize: 6,
                      annotations: {
                        alwaysOutside: true,
                        textStyle: {
                          fontSize: 10,
                          color: chartColors[0] || '#1e2329',
                          auraColor: '#ffffff',
                          bold: true,
                        },
                        stem: { length: 3, color: 'transparent' },
                        boxStyle: {
                          stroke: chartColors[0] || '#e0e0e0',
                          strokeWidth: 1,
                          rx: 3,
                          ry: 3,
                          gradient: {
                            color1: '#ffffff',
                            color2: '#ffffff',
                            x1: '0%',
                            y1: '0%',
                            x2: '100%',
                            y2: '100%',
                          },
                        },
                      },
                      tooltip: {
                        textStyle: { fontSize: 11 },
                        showColorCode: false,
                      },
                    }}
                  />
                </div>
              </Card>
            </Col>
          </Row>

          <Row className={transferCss.footerSection}>
          <Col
            className={classnames(
              transferCss.footerCardCol,
              transferCss.cardAtendPadding
            )}
          >
            <Card className={transferCss.footerCard}>
              <Card.Body>
                <Card.Title>Atendimentos</Card.Title>
                <Card.Subtitle className="mb-2 text-muted">
                  Atendimentos realizados no período
                </Card.Subtitle>
                <Card.Text>
                  {indicators.totalAttendances} atendimentos
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>

          <Col
            className={classnames(
              transferCss.footerCardCol,
              transferCss.cardPmrPadding
            )}
          >
            <Card className={transferCss.footerCard}>
              <Card.Body>
                <Card.Title>PMR</Card.Title>
                <Card.Subtitle className="mb-2 text-muted">
                  Prazo médio de recebimento
                </Card.Subtitle>
                <Card.Text>{indicators.averageReceiptPeriod} dias</Card.Text>
              </Card.Body>
            </Card>
          </Col>

          <Col
            className={classnames(
              transferCss.footerCardCol,
              transferCss.cardTicketPadding
            )}
          >
            <Card className={transferCss.footerCard}>
              <Card.Body>
                <Card.Title>Ticket</Card.Title>
                <Card.Subtitle className="mb-2 text-muted">
                  Ticket médio de receita
                </Card.Subtitle>
                <Card.Text>
                  <CurrencyLabel>
                    {indicators.averageRevenueTicket}
                  </CurrencyLabel>
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>
          </Row>
        </div>
      )}
    </Container>
  )
}

export default Transfers
